// ══════════════════════════════════════════════════════════════════════════════════════════════
//  «ENTRAR Y DESBLOQUEAR CON HUELLA O CARA» · UNA SOLA COSA PARA LA PERSONA, DOS MECANISMOS POR DEBAJO  (5-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «En Mi perfil hay 2 tarjetas que en teoría hacen lo mismo [...] no me permiten registrar mi huella, y debería
//  poder usarlo tanto cuando abro la app como cuando se cierra la sesión [...] También debería ofrecerme registrarlo si aún
//  no lo tengo activo en mi móvil».
//
//  Para quien la usa es UNA cosa: «entro y desbloqueo con el dedo o la cara en este dispositivo». Por debajo hay dos, y
//  cuál toca lo decide DÓNDE se está:
//   · en la APP del móvil (Capacitor) → la LLAVE DEL MÓVIL (services/entradaBiometrica): Face ID / Touch ID / huella del
//     propio sistema, guardada en su llavero. Necesita la app compilada con su complemento; una app vieja (la publicada
//     antes del 3-oct) no lo trae, y AHÍ ESTABA el «no me deja»: se le ofrecía la huella del navegador (WebAuthn), que
//     dentro de una app el iPhone no deja usar (solo a apps asociadas a biango.app) y Android no trae. Ahora se le dice
//     que actualice la app, en vez de ofrecerle algo que falla;
//   · en el NAVEGADOR (ordenador con Windows Hello / Touch ID, Safari o Chrome del móvil) → la credencial WebAuthn
//     (services/huella): la pantalla de bloqueo y, desde hoy, también la de entrar cuando la sesión se ha acabado.
//  Las dos sirven para ENTRAR (pantalla de entrar) y para DESBLOQUEAR (pantalla de bloqueo); las dos las quita la persona
//  en Mi perfil y la administración en Seguridad; y «Móvil robado», cambiar la contraseña o desactivar la cuenta las apagan.
import api from './api'
import { hayBiometriaEnEstaApp, estadoEnEsteMovil, guardarLlaves, borrarLlaves, publicasDeEsteMovil, noPreguntar, marcarNoPreguntar, plataforma, DIAS_SIN_PREGUNTAR } from './entradaBiometrica'
import { hayLector, registrarHuella, nombreDispositivo, huellaDeEsteAparato, olvidarHuellaDeEsteAparato, explicarFalloHuella, credencialesDeEsteNavegador, entrarConHuella, prepararEntradaConHuella } from './huella'
import { navegadorPospuesto, posponerEnNavegador } from '../utils/huellaPospuesta'
import { t } from '../utils/i18n.js'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// ¿Corre dentro de la app del móvil (sea cual sea su versión)?
export function enLaApp(w = (typeof window !== 'undefined' ? window : null)) {
  try { const c = w && w.Capacitor; return !!(c && typeof c.isNativePlatform === 'function' && c.isNativePlatform()) } catch { return false }
}

/**
 * Lo que hay en ESTE dispositivo. Nunca lanza.
 *   tipo: 'app' (app con el complemento) · 'app-vieja' (app sin él: hay que actualizarla) · 'navegador' · 'sin-lector'
 *   puede: se puede activar aquí ahora · activado: ya está activado aquí · nombre: «Face ID», «la huella»…
 *   motivo: por qué no se puede, dicho para la persona.
 */
export async function esteDispositivo(agenteId) {
  if (enLaApp()) {
    if (!hayBiometriaEnEstaApp()) {
      return { tipo: 'app-vieja', puede: false, activado: false, nombre: t('services.huellaOCara.face_id_o_la_huella', 'Face ID o la huella'),
        motivo: t('services.huellaOCara.esta_version_de_la_app', 'Esta versión de la app todavía no sabe usar Face ID ni la huella. En cuanto se publique la nueva en App Store y Google Play y la actualices, lo activas aquí con un toque. Mientras, desbloquea con tu PIN o tu contraseña.') }
    }
    const e = await estadoEnEsteMovil('equipo')
    let servidor = false
    try { servidor = (await api.get('/auth/biometria/disponible')).data?.disponible === true } catch { servidor = false }
    if (!e.puede) {
      return { tipo: 'app', puede: false, activado: !!e.activada, nombre: e.nombre,
        motivo: t('services.huellaOCara.este_movil_no_tiene_face', 'Este móvil no tiene Face ID, Touch ID ni huella configurados. Configúralos en los ajustes del móvil y vuelve aquí.') }
    }
    return { tipo: 'app', puede: servidor && !e.activada, activado: !!e.activada, nombre: e.nombre, servidor,
      motivo: servidor ? '' : t('services.huellaOCara.todavia_no_esta_disponible_en', 'Todavía no está disponible en el servidor.') }
  }
  if (!(await hayLector())) {
    return { tipo: 'sin-lector', puede: false, activado: false, nombre: t('services.huellaOCara.la_huella_o_la_cara', 'la huella o la cara'),
      motivo: t('services.huellaOCara.este_navegador_no_encuentra_lector', 'Este navegador no encuentra lector de huella ni de cara en este aparato. En un ordenador con Windows Hello o un Mac con Touch ID, o en el móvil, sí se puede.') }
  }
  let mias = { disponible: false, lista: [] }
  try { mias = (await api.get('/auth/biometria/web/mias')).data } catch { mias = { disponible: false, lista: [] } }
  const mia = huellaDeEsteAparato(agenteId)
  const activado = !!(mia && (mias.lista || []).some((c) => c.credential_id === mia))
  if (mia && !activado && mias.disponible) olvidarHuellaDeEsteAparato(agenteId)
  return { tipo: 'navegador', puede: mias.disponible !== false && !activado, activado, nombre: t('services.huellaOCara.la_huella_o_la_cara', 'la huella o la cara'),
    motivo: mias.disponible === false ? t('services.huellaOCara.todavia_no_esta_disponible_en', 'Todavía no está disponible en el servidor.') : '' }
}

/** Activar en ESTE dispositivo. En la app hace falta la contraseña (la llave se da con ella); en el navegador, no. */
export async function activarAqui({ tipo, email, password, agenteId }) {
  if (tipo === 'app') {
    let creadas = null
    try {
      const { data } = await api.post('/auth/biometria/activar', { email, password, plataforma: plataforma() })
      creadas = data
      await guardarLlaves('equipo', { tokens: data.tokens, publicos: data.publicos })
      marcarNoPreguntar('equipo', false)
      return { ok: true }
    } catch (e) {
      // Creada en el servidor y no guardada en el móvil (se canceló la huella): se apaga, no se deja suelta.
      if (creadas?.publicos?.length) api.post('/auth/biometria/olvidar', { tokens_publicos: creadas.publicos }).catch(() => {})
      throw e
    }
  }
  if (tipo === 'navegador') {
    await registrarHuella(nombreDispositivo(), agenteId)
    posponerOferta(agenteId, false)
    return { ok: true }
  }
  throw new Error(t('services.huellaOCara.aqui_no_se_puede_activar', 'Aquí no se puede activar'))
}

/** Quitar de ESTE dispositivo (y apagarla en el servidor). */
export async function quitarAqui({ tipo, agenteId, filaWeb }) {
  if (tipo === 'app') {
    const publicos = await borrarLlaves('equipo')
    if (publicos.length) await api.post('/auth/biometria/olvidar', { tokens_publicos: publicos }).catch(() => {})
    marcarNoPreguntar('equipo', true)
    return
  }
  if (tipo === 'navegador') {
    if (filaWeb) await api.delete('/seguridad/huella/' + filaWeb)
    olvidarHuellaDeEsteAparato(agenteId)
    posponerOferta(agenteId, true)
  }
}

/** Los dispositivos de la cuenta, los dos tipos juntos, para la lista con «Quitar». */
export async function misDispositivos() {
  const out = { disponible: false, lista: [] }
  try {
    const r = (await api.get('/auth/biometria/mis-dispositivos')).data
    if (r && r.disponible) {
      out.disponible = true
      for (const d of r.dispositivos || []) out.lista.push({ clave: 'app-' + d.id, tipo: 'app', id: d.id, nombre: d.nombre || 'Móvil', donde: t('services.huellaOCara.app_del_movil', 'App del móvil'), desde: d.desde, ultimo_uso: d.ultimo_uso, ref: d.ref })
    }
  } catch { /* sin la llave del móvil */ }
  try {
    const r = (await api.get('/auth/biometria/web/mias')).data
    if (r && r.disponible) {
      out.disponible = true
      for (const c of r.lista || []) out.lista.push({ clave: 'web-' + c.id, tipo: 'navegador', id: c.id, nombre: c.nombre || 'Navegador', donde: 'Navegador', desde: c.alta, ultimo_uso: c.ultimo_uso, credential_id: c.credential_id })
    }
  } catch { /* sin WebAuthn */ }
  return out
}

/** Quitar UNO de la lista (de este dispositivo o de otro: el móvil perdido se quita desde el ordenador). */
export async function quitarDeLaLista(d) {
  if (d.tipo === 'app') await api.delete('/auth/biometria/mis-dispositivos/' + d.id)
  else await api.delete('/seguridad/huella/' + d.id)
}

// Qué dispositivos de la lista son ESTE (para escribir «este dispositivo» al lado).
export async function marcasDeEsteDispositivo(agenteId) {
  const marcas = new Set()
  try { for (const p of await publicasDeEsteMovil('equipo')) marcas.add('app:' + String(p).slice(0, 8)) } catch { /* nada */ }
  const mia = huellaDeEsteAparato(agenteId)
  if (mia) marcas.add('web:' + mia)
  return marcas
}
export const esDeEste = (marcas, d) => (d.tipo === 'app' ? marcas.has('app:' + d.ref) : marcas.has('web:' + d.credential_id))

// ── «AHORA NO»: 30 días, por persona y dispositivo. En la app, la marca de la llave del móvil (noPreguntar('equipo'), la
// misma que el «Ahora no» de la pantalla de entrar); en el navegador, utils/huellaPospuesta (la lee también la pantalla
// de bloqueo).
export function ofertaPospuesta(tipo, agenteId, ahora = Date.now()) {
  if (tipo === 'app') return noPreguntar('equipo', ahora)
  return navegadorPospuesto(agenteId, ahora)
}
export function posponerOferta(agenteId, si = true, ahora = Date.now(), tipo = 'navegador') {
  if (tipo === 'app') { marcarNoPreguntar('equipo', si, ahora); return }
  posponerEnNavegador(agenteId, si, ahora)
}

// Que la oferta salga UNA vez por arranque de la app (la pantalla de entrar y la de dentro no la repiten).
const OFRECIDA = 'biango_huella_ofrecida'
export function yaOfrecida() { try { return sessionStorage.getItem(OFRECIDA) === '1' } catch { return true } }
export function apuntarOfrecida() { try { sessionStorage.setItem(OFRECIDA, '1') } catch { /* nada */ } }

// Por qué no se ha podido activar, a Seguridad, «Registro de accesos» (nunca la huella, ni la contraseña, ni llaves).
export function contarFallo(tipo, e) {
  api.post('/auth/biometria/fallo', { mecanismo: tipo, motivo: (e && (e.name || (e.response && e.response.status))) || '', mensaje: (e && e.message) || '' }).catch(() => {})
}

// La pantalla de entrar del navegador (pages/Login) las toma de aquí: un solo sitio para lo de la huella o la cara.
export { explicarFalloHuella, DIAS_SIN_PREGUNTAR, credencialesDeEsteNavegador, entrarConHuella, prepararEntradaConHuella }
